// The Website studio's pages: real records she creates, plus a Homepage that
// appears on its own when My Website has written one.
//
// MY WEBSITE IS A MOMENTUM MODULE, NOT AN ACCELERATOR ONE.
// `constants/modules.js` puts `website` in MOMENTUM_MODULES; it is not in
// ACCELERATOR_MODULES. So nothing here is "pulled across from the Accelerator" —
// the chat module and this studio are two halves of the same Momentum tool. The
// stored `source` value is still the string "accelerator" because rows in
// Supabase already carry it and rewriting them buys nothing; treat it as an
// opaque "this page made itself" marker, and never surface that word in the UI.
//
// WHY A PAGE IS A RECORD AND NOT A CONSTANT
// The studio used to render a fixed list of four page names and read the locked
// copy behind each one. That's a viewer, not a website — she can't add a fifth
// page, can't say which are published, can't give one an address. These rows are
// hers; the locked copy stays where it is and is read through `sourceSection`.
//
// ONLY THE HOMEPAGE APPEARS ON ITS OWN (her call, 2026-08-16). The other three
// standard pages are offered in the Add page menu instead of being created for
// her. Four rows conjured up front, three of them empty, read as though content
// had been pulled in when none had.
//
// NO `website_seeded` FLAG, UNLIKE OFFERS. The offers pull needs one because a
// generated id gives Postgres nothing to collide on, so a second browser inserts
// a duplicate Signature Offer. `website_pages` has `unique (clerk_user_id, slug)`
// — a natural key — so a second browser's insert FAILS instead of duplicating.
// The empty-table guard below is the first line; the constraint is the real one.

import { useCallback, useEffect, useMemo, useRef } from "react";
import { createRecordStore } from "./recordStore";
import { reorderWrites } from "./reorder";
import { LANDING_SITE, MAIN_SITE } from "./useSites";

// `id`, `createdAt`, `updatedAt` are handled by recordStore — not listed.
// `source` / `sourceSection` are added by supabase/0004_website_page_source.sql,
// `sortOrder` by supabase/0005_website_page_order.sql, `kind` by
// supabase/0006_website_page_kind.sql.
// `siteId` by supabase/0015_sites_and_courses.sql — which site this page
// belongs to. Nullable in Postgres, and rows written by a browser still running
// the code from before the switcher will arrive with it empty; see the note on
// `pages` in the hook for where those show up rather than vanishing.
// `offerId` (0015) is the connector: which offer this page serves. It puts the
// page under that offer's "Supported by".
/* `seoTitle` and `seoDescription` (supabase/0027) are OVERRIDES: null means she
   has not written one, and the site brief derives it. See the migration. */
/* `style` (supabase/0039) is the page's look and word room (utils/siteStyles).
   SAFE BEFORE THE MIGRATION RUNS, and that is deliberate: a save only sends a
   field the record actually holds, and a page loaded from a table with no
   `style` column holds none. The one write that sets it is the style picker's
   own (MomentumHub `savePageStyle`), which never touches the open page's draft
   unless it succeeded — so a failed pick cannot ride along on her next save of
   the page name and fail that too. */
export const WEBSITE_PAGE_FIELDS = ["slug", "title", "status", "kind", "source", "sourceSection", "sortOrder", "siteId", "offerId", "seoTitle", "seoDescription", "style"];

export const WEBSITE_SOURCE = "accelerator";

/* The Hero Statement is the one locked section that is NOT a page — the module
   writes it as the line the homepage is built around. It has always been shown
   as part of the Homepage rather than as a fifth page, and that stays true here;
   a page called "Hero Statement" would be a page she can't publish. */
export const HERO_SECTION = "Hero Statement";

/* A PAGE HAS THREE SEPARATE THINGS, and conflating them is what this fixes:
     title   hers, renameable — "About Page" becoming "Meet Tevra"
     address hers, editable — /about
     KIND    the sort of page it is — durable, and not something she edits

   Kind is what the copy is drawn from. Before 0006 the join was stored per row as
   `source_section: "About Page"`, which hard-coded WHERE the copy comes from onto
   every page at the moment it was created — so deciding later that About should
   draw on My Story would leave every existing page pointing at the old section.
   With the kind stored instead and the source looked up in code, that decision is
   this table and nothing else.

   `title` and `slug` here are only the DEFAULTS a new page of that kind starts
   with. She renames it the second after and it stays the same kind. */
export const PAGE_KINDS = [
  { id: "home", title: "Homepage", slug: "home", hint: "The first page anyone lands on", sourceSection: "Homepage", sites: [MAIN_SITE] },
  { id: "services", title: "Services Page", slug: "services", hint: "What you offer and what it costs", sourceSection: "Services Page", sites: [MAIN_SITE] },
  { id: "about", title: "About Page", slug: "about", hint: "Your story and why you do this", sourceSection: "About Page", sites: [MAIN_SITE] },
  { id: "contact", title: "Contact Page", slug: "contact", hint: "How people reach you or book time", sourceSection: "Contact Page", sites: [MAIN_SITE] },
  /* THE LANDING SITE'S TWO. Her decision 8: a landing page is the same object as
     a website page, and the only thing the site's kind changes is which pages
     its Add menu offers. No `sourceSection` on either — My Website writes a
     whole site's copy, not one offer's pitch, so Brill drafts these in the page
     itself the way it drafts a page she added herself.

     THIS PAIR IS A PROPOSAL, and the spec says so: "a landing site's list is the
     open design item: probably Landing page and Thank-you page, with Other as
     always" (6.8). It is one array to change when she has a view. */
  { id: "landing", title: "Landing Page", slug: "landing", hint: "One offer, one argument, one button", sourceSection: null, sites: [LANDING_SITE] },
  { id: "thanks", title: "Thank-you Page", slug: "thank-you", hint: "What they see after they say yes — and what to do next", sourceSection: null, sites: [LANDING_SITE] },
  /* A page of her own. No source — Brill writes its copy in the page itself
     rather than pulling it from a locked section. Offered on every site kind,
     which is why it is in no `sites` list: the Add menu adds it by hand. */
  { id: "other", title: "", slug: "", hint: "A page of your own — name it and give it an address", sourceSection: null },
];

export const OTHER_PAGE_KIND = "other";

const PAGE_KIND_BY_ID = new Map(PAGE_KINDS.map((k) => [k.id, k]));

/* The four pages My Website writes, in the order it writes them — the standard
   pages offered in the Add page menu. Only the first appears on its own.
   Derived from PAGE_KINDS so there is one list, not two that must agree. */
export const SEED_PAGES = standardPagesFor(MAIN_SITE);

/**
 * The standard pages a site of this kind offers in its Add page menu.
 *
 * Read from PAGE_KINDS rather than kept as a second list per kind, so a page's
 * hint, its address and which menu it appears in are all one decision. A kind
 * with no `sites` (Other) is in nobody's standard list — the menu adds it by
 * hand, because it is the escape hatch rather than a recommendation.
 */
export function standardPagesFor(kind = MAIN_SITE) {
  return PAGE_KINDS
    .filter((k) => (k.sites || []).includes(kind))
    .map((k) => ({ kind: k.id, slug: k.slug, title: k.title, sourceSection: k.sourceSection }));
}

/**
 * Which kind of page this is.
 *
 * Rows written before 0006 have no `kind`, so it's recovered from the section
 * they were joined to — the value that HELD the kind before there was a column
 * for it. Falls to `other`, which is right for a page she made herself.
 */
export function pageKind(page) {
  const stored = String(page?.kind || "").toLowerCase();
  if (PAGE_KIND_BY_ID.has(stored)) return stored;
  const section = page?.sourceSection;
  const match = section ? PAGE_KINDS.find((k) => k.sourceSection === section) : null;
  return match ? match.id : OTHER_PAGE_KIND;
}

export function pageKindDef(page) {
  return PAGE_KIND_BY_ID.get(pageKind(page)) || PAGE_KIND_BY_ID.get(OTHER_PAGE_KIND);
}

/**
 * Extra locked Accelerator sections the drafting chat should carry, on top of
 * what website copy always gets (`CONTEXT_SCOPES.website_copy` in
 * momentumContext.js).
 *
 * HER ASK, 2026-09-15: "make sure the about me section has access to my story."
 * My Story was in no website scope at all, so Brill was drafting the pages
 * written out of her founder story with the story itself absent.
 *
 * KEYED ON BOTH THE PAGE AND THE SECTION, which is her correction the same day:
 * "the about section as well on the homepage needs it." A whole About page is
 * about her, so every section of one gets it — but most homepages carry an
 * "About me" block, and that block needs the story while the banner, the
 * services strip and the FAQ beside it do not. Page alone would have starved
 * it; section alone would have starved an About page's banner, which is a line
 * about her written above a page about her.
 *
 * The five NARRATIVE sections, never all six. "My Archetype & My Personal
 * Style" is left out because it already reaches Brill twice — Voice Profile
 * carries it, and the archetype rides as its own field on every call.
 *
 * EVERYWHERE ELSE THIS IS NULL, and that is the point: these five are a lot of
 * her prose and it rides along on every message of the conversation. Her story
 * has no business in the pricing on a Services page.
 */
const STORY_FOR_COPY = {
  story: [
    "What It Felt Like",
    "When the Chapter Closed",
    "The Path You Chose",
    "What a Hard Day Looked Like",
    "What It Cost & What You Carried",
  ],
};

/** By page kind — every section of this page gets it. */
export const PAGE_CONTEXT_EXTRAS = { about: STORY_FOR_COPY };

/** By SECTION kind — this block gets it whatever page it sits on. Keyed to the
 *  `about` kind in websiteSectionKinds.js, the one labelled "About me". */
export const SECTION_CONTEXT_EXTRAS = { about: STORY_FOR_COPY };

/**
 * What this page AND this section together ask for, merged per module so one
 * can never quietly drop the other's sections.
 *
 * `section` is optional: with no section this answers for the page alone, which
 * is what a whole-page pass wants.
 */
export function contextScopeFor(page, section) {
  const fromPage = PAGE_CONTEXT_EXTRAS[pageKind(page)] || null;
  const fromSection = SECTION_CONTEXT_EXTRAS[String(section?.sectionType || "").toLowerCase()] || null;
  if (!fromPage) return fromSection;
  if (!fromSection) return fromPage;
  return Object.entries(fromSection).reduce((acc, [id, names]) => ({
    ...acc,
    [id]: [...new Set([...(acc[id] || []), ...(names || [])])],
  }), { ...fromPage });
}

/**
 * The locked My Website section a page's copy is drawn from, or null.
 *
 * Looked up from the KIND rather than read off the row, so the mapping is a code
 * decision that applies to every page at once. The stored `sourceSection` is
 * still honoured as a fallback: a row whose kind can't be recovered keeps
 * whatever join it was created with rather than silently losing its copy.
 */
export function sourceSectionFor(page) {
  return pageKindDef(page).sourceSection || page?.sourceSection || null;
}

/** The locked Accelerator copy behind a page, or "" if there is none.
 *  Homepage is the special case: hero line first, then the page body. */
export function pageContent(page, outputs = {}) {
  const section = sourceSectionFor(page);
  if (!section) return "";
  const body = String(outputs?.[section] || "").trim();
  if (section !== "Homepage") return body;
  const hero = String(outputs?.[HERO_SECTION] || "").trim();
  return [hero && `Hero — ${hero}`, body].filter(Boolean).join("\n\n");
}

/** Is there anything locked in My Website yet? Gates the seed — pulling four
 *  empty pages the moment she opens the panel is noise, not a head start. */
export function hasLockedWebsite(outputs = {}) {
  if (String(outputs?.[HERO_SECTION] || "").trim()) return true;
  return SEED_PAGES.some((p) => String(outputs?.[p.sourceSection] || "").trim());
}

/**
 * Her pages in site order. Pulled out of the hook so it can be tested: the bug
 * it fixes is invisible from the outside — the write succeeds, Supabase is
 * correct, and only the screen is stale — so it needs pinning rather than
 * eyeballing. Rows missing `sort_order` (written before 0005 ran) sort as 0 and
 * keep their relative order, since Array.sort is stable.
 */
export function byPageOrder(pages = []) {
  return [...(pages || [])].sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0));
}

/**
 * The pages of one site.
 *
 * Pulled out of the hook so it can be tested, because the bug it guards is
 * invisible from the outside: the page is in Supabase, the write succeeded, and
 * it simply never appears on a screen.
 *
 * `siteId` null means the sites have not loaded yet, and the answer is an empty
 * list rather than everything — the caller says "Loading your pages…" instead
 * of showing another site's pages for a frame.
 *
 * `adoptUnsited` is true for her FIRST site only. `site_id` is nullable and
 * 0015's backfill filled it for every page that existed, but a browser tab left
 * open on the code from before the switcher writes one with no site. On every
 * site that page would be listed twice over; on none it would be lost silently.
 * On the first, it is somewhere she can find it and move it.
 */
export function pagesOfSite(pages = [], siteId = null, adoptUnsited = false) {
  if (!siteId) return [];
  return (pages || []).filter((p) => p?.siteId === siteId || (adoptUnsited && !p?.siteId));
}

/** The next free slot at the bottom of her page order. */
export function nextOrder(pages = []) {
  return (pages || []).reduce((max, p) => Math.max(max, (p?.sortOrder ?? 0) + 1), 0);
}

/** Which of the four standard pages aren't in her list, matched on slug — the
 *  column Postgres makes unique, not the title she's free to change. */
export function missingSeedPages(pages = [], kind = MAIN_SITE) {
  const have = new Set((pages || []).map((p) => String(p?.slug || "").trim().toLowerCase()));
  return standardPagesFor(kind).filter((p) => !have.has(p.slug));
}

/** A page address from whatever she typed. Not clever on purpose: it strips a
 *  leading slash, lowercases, and turns runs of anything else into one dash. */
export function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^\/+/, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Would saving this page collide with another one?
 *
 * Checked here rather than left to the unique constraint because the constraint
 * fails with a Postgres string ("duplicate key value violates ...") that means
 * nothing to her, and it fails AFTER she's hit save on a form she then has to
 * work out how to fix.
 */
export function slugTaken(slug, pages = [], selfId = null) {
  const s = slugify(slug);
  if (!s) return false;
  return (pages || []).some((p) => p.id !== selfId && slugify(p?.slug) === s);
}

/* Ordered by `sort_order`, not newest-first. A list she adds to wants the newest
   at the top; a website wants Homepage above Contact, because that's the order a
   visitor meets them. Same reasoning as the blocks inside a page. */
const usePageRecords = createRecordStore({
  table: "website_pages",
  fields: WEBSITE_PAGE_FIELDS,
  idPrefix: "wpg",
  orderBy: "sort_order",
  ascending: true,
});

/**
 * @param websiteOutputs  her locked My Website sections, from App's mergedOutputs
 * @param siteId          which site's pages this is about. Every row loads —
 *                        one query for the account — and the list is narrowed
 *                        here, because the Table shows word counts and section
 *                        dots per page and a query per site would be a request
 *                        inside a render loop. Null while the sites are still
 *                        loading, and then the list is empty on purpose: the
 *                        caller shows "Loading your pages…" rather than
 *                        flashing "No pages yet", which reads as loss.
 * @param siteKind        `main` or `landing` — which standard pages this site's
 *                        Add menu offers, and whether the automatic Homepage
 *                        applies at all.
 * @param adoptUnsited    show pages with NO site here. True for her first site
 *                        only. See the note on `pages`.
 * @param enabled         gate for the automatic seed. Callers that don't have
 *                        her locked content yet must leave this false, or the
 *                        seed decides there's nothing to pull and never retries
 *                        for that session.
 */
export function useWebsitePages({
  websiteOutputs = {}, siteId = null, siteKind = MAIN_SITE, adoptUnsited = false, enabled = true,
} = {}) {
  const { rows, loading, error, save, remove, reload, clearError, newId } = usePageRecords();

  /* Sorted here, not just in the query. `recordStore.save()` replaces a row IN
     PLACE in local state — right for every table whose order never changes, and
     wrong for this one: swapping two `sort_order` values wrote to Supabase but
     left the array exactly as it was, so the arrows looked broken until a
     reload. The query's ORDER BY only applies to a fresh load. */
  const ordered = useMemo(() => byPageOrder(rows), [rows]);

  /* THE UNSITED PAGE, and why it is adopted rather than filtered away.
     `site_id` is nullable, and 0015's backfill filled it for every page that
     existed — but a browser tab left open on the code from before the switcher
     writes a page with no site, and that page would then belong to no list and
     appear on no screen. Silently. That is the exact failure mode CLAUDE.md's
     facts check is for: "nothing — it just quietly does not happen".

     So her FIRST site shows them alongside its own. Not every site, or one page
     would appear twice; not none, or it would appear nowhere. It stays listed
     under the first site until she moves it, which is visible and recoverable
     rather than lost. */
  const pages = useMemo(
    () => pagesOfSite(ordered, siteId, adoptUnsited),
    [ordered, siteId, adoptUnsited],
  );

  /* Creates one of the four standard pages. Used by the Add page menu, and by
     the automatic Homepage below — the same call either way, so a page she adds
     by hand is identical to the one that appeared on its own. */
  const addStandardPage = useCallback(async (slug) => {
    /* Looked up in THIS site's standard list, not the main-site one: a landing
       site offers different pages, and a slug from the wrong list would create a
       page the menu never offered. */
    const p = standardPagesFor(siteKind).find((s) => s.slug === slug);
    if (!p || !siteId) return null;
    return save({
      id: newId(),
      siteId,
      slug: p.slug,
      title: p.title,
      kind: p.kind,
      /* Still written, though `sourceSectionFor` now reads the kind: a row that
         predates a kind being renamed or retired keeps a working join, and
         nothing outside this app has to know the lookup. */
      sourceSection: p.sourceSection,
      source: WEBSITE_SOURCE,
      status: "draft",
      sortOrder: nextOrder(pages),
    });
  }, [pages, save, newId, siteId, siteKind]);

  /** Drag a page from one position to another. Writes, because `sort_order` is
   *  what the next load reads — reordering in state alone survives until reload
   *  and no further.
   *
   *  RENUMBERS rather than swapping neighbours (rule 10, spec R5), which is the
   *  same call course modules and a page's own sections already make. The swap
   *  this replaces could not express "drag the first page to the end", and it
   *  needed a special case for rows that shared a `sort_order` — renumbering
   *  repairs those on the way past. `reorderWrites` returns only the rows whose
   *  position actually changed, so a one-place move is still two writes. */
  const reorderPages = useCallback(async (from, to) => {
    let ok = true;
    for (const p of reorderWrites(pages, from, to)) if (!(await save(p))) ok = false;
    return ok;
  }, [pages, save]);

  /* The automatic Homepage. Four guards, and each one is load-bearing:
       - `enabled` / `loading` — creating against a list that hasn't arrived would
         insert a page she already has.
       - `error` — a failed load leaves `rows` empty, which is NOT the same as an
         empty table. Without this, one dropped connection makes a second one.
       - `pages.length` + `madeOnce` — the table must be COMPLETELY empty, and
         only once per session, or deleting her Homepage would bring it back
         every time she opened the panel. Adding it again is the menu's job.
         (Gap, accepted: delete every page AND reload the app and it returns.)
     Gated on there being a written Homepage: an empty page appearing on its own
     is clutter, whereas one carrying her copy is a head start. */
  const creating = useRef(false);
  const madeOnce = useRef(false);
  useEffect(() => {
    if (!enabled || loading || error || creating.current || madeOnce.current || pages.length) return;
    /* Not on a landing site, and not before the sites have loaded. A landing
       site's standard list has no Homepage in it, so conjuring one would be this
       tool creating a page its own menu never offers. */
    if (!siteId || siteKind !== MAIN_SITE) return;
    if (!hasLockedWebsite(websiteOutputs)) return;
    creating.current = true;
    madeOnce.current = true;   // deleting the Homepage must not bring it back
    addStandardPage("home")
      .then((made) => {
        /* A losing race with another browser fails the unique constraint. That's
           the constraint doing its job, not something she did — so it reloads
           rather than showing her a Postgres error about duplicate keys. */
        if (!made) { clearError(); reload(); }
      })
      .finally(() => { creating.current = false; });
  }, [enabled, loading, error, pages.length, siteId, siteKind, websiteOutputs, addStandardPage, clearError, reload]);

  return {
    pages, loading, error, clearError,
    /* EVERY row, not just this site's. The landing table counts the
       children of each site, so it needs the whole list — and it costs
       nothing, because the store has always loaded every row and
       filtered in memory. Exposing it beats a second query per row
       inside a render loop, which is the shape check 4 exists to
       catch. */
    allPages: ordered,
    savePage: save,
    deletePage: remove,
    reload,
    newPageId: newId,
    addStandardPage,
    reorderPages,
    nextPageOrder: nextOrder(pages),
    /* Which of THIS site kind's standard pages it is still missing — what the
       Add menu greys out. A landing site is missing a Landing page, never a
       Services page. */
    missing: missingSeedPages(pages, siteKind),
    standardPages: standardPagesFor(siteKind),
  };
}
